/* __next_internal_client_entry_do_not_use__ ITEM_GAP_DEFAULT,ITEM_PER_SLIDE_DEFAULT,Carousel auto */ import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable complexity */ import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { IconButton } from "@faire/web--source/slate/IconButton/IconButton";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { Flex } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { isResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CarouselDots } from "./__internal__/CarouselDots";
import { CTATile, shouldAddCTATile } from "./__internal__/CTATile";
export var ITEM_GAP_DEFAULT = {
    mobileAndAbove: 16,
    tabletAndAbove: 16
};
export var ITEM_PER_SLIDE_DEFAULT = {
    mobileAndAbove: 2,
    tabletAndAbove: 3,
    desktopAndAbove: 5
};
var PEEK_NEXT_PERCENT_DEFAULT = {
    mobileAndAbove: 10,
    tabletAndAbove: 0,
    desktopAndAbove: 0
};
var PEEK_PREVIOUS_PERCENT_DEFAULT = {
    mobileAndAbove: 0,
    tabletAndAbove: 0,
    desktopAndAbove: 0
};
var DRAG_FREE_DEFAULT = {
    mobileAndAbove: true,
    tabletAndAbove: false,
    desktopAndAbove: false
};
var LOOP_DEFAULT = {
    mobileAndAbove: false,
    tabletAndAbove: true,
    desktopAndAbove: true
};
var SHOW_NAVIGATION_ARROWS_DEFAULT = {
    mobileAndAbove: false,
    tabletAndAbove: true
};
var getInitialRenderFlagsForViewport = function(key, param) {
    var numItems = param.numItems, itemsPerSlideGenerated = param.itemsPerSlideGenerated, peekNextPercentGenerated = param.peekNextPercentGenerated, peekPreviousPercentGenerated = param.peekPreviousPercentGenerated;
    return _to_consumable_array(Array(itemsPerSlideGenerated[key] + (peekNextPercentGenerated[key] > 0 ? 1 : 0)).keys()).concat([
        peekPreviousPercentGenerated[key] > 0 ? numItems - 1 : undefined
    ]).filter(isNotUndefined);
};
/**
 * Gets the indicies of the slides that should be rendered initially
 */ var getInitialRenderFlags = function(viewport, arg) {
    if (viewport.isXLargeAndAbove) {
        return getInitialRenderFlagsForViewport("xLargeAndAbove", arg);
    } else if (viewport.isDesktopAndAbove) {
        return getInitialRenderFlagsForViewport("desktopAndAbove", arg);
    } else if (viewport.isTabletAndAbove) {
        return getInitialRenderFlagsForViewport("tabletAndAbove", arg);
    } else {
        return getInitialRenderFlagsForViewport("mobileAndAbove", arg);
    }
};
var shouldAddCTATileWithViewport = function(arr, data, itemsPerSlide, viewport) {
    var itemsPerSlideWithViewport = itemsPerSlide.xLargeAndAbove;
    if (viewport.isDesktop) {
        itemsPerSlideWithViewport = itemsPerSlide.desktopAndAbove;
    } else if (viewport.isTablet) {
        itemsPerSlideWithViewport = itemsPerSlide.tabletAndAbove;
    } else if (viewport.isMobile) {
        itemsPerSlideWithViewport = itemsPerSlide.mobileAndAbove;
    }
    return shouldAddCTATile(arr, data, itemsPerSlideWithViewport);
};
var generateResponsiveValuesFromProps = function(prop) {
    var mobileAndAbove = isResponsiveProp(prop) ? prop.mobileAndAbove : prop;
    var _ref;
    var tabletAndAbove = (_ref = isResponsiveProp(prop) ? prop.tabletAndAbove : prop) !== null && _ref !== void 0 ? _ref : mobileAndAbove;
    var _ref1;
    var desktopAndAbove = (_ref1 = isResponsiveProp(prop) ? prop.desktopAndAbove : prop) !== null && _ref1 !== void 0 ? _ref1 : tabletAndAbove;
    var _ref2;
    var xLargeAndAbove = (_ref2 = isResponsiveProp(prop) ? prop.xLargeAndAbove : prop) !== null && _ref2 !== void 0 ? _ref2 : desktopAndAbove;
    return {
        mobileAndAbove: mobileAndAbove,
        tabletAndAbove: tabletAndAbove,
        desktopAndAbove: desktopAndAbove,
        xLargeAndAbove: xLargeAndAbove
    };
};
export var Carousel = function(props) {
    var items = props.items, renderItem = props.renderItem, _props_itemsPerSlide = props.itemsPerSlide, itemsPerSlide = _props_itemsPerSlide === void 0 ? ITEM_PER_SLIDE_DEFAULT : _props_itemsPerSlide, _props_itemGap = props.itemGap, itemGap = _props_itemGap === void 0 ? ITEM_GAP_DEFAULT : _props_itemGap, _props_peekNextPercent = props.peekNextPercent, peekNextPercent = _props_peekNextPercent === void 0 ? PEEK_NEXT_PERCENT_DEFAULT : _props_peekNextPercent, _props_peekPreviousPercent = props.peekPreviousPercent, peekPreviousPercent = _props_peekPreviousPercent === void 0 ? PEEK_PREVIOUS_PERCENT_DEFAULT : _props_peekPreviousPercent, _props_loop = props.loop, loop = _props_loop === void 0 ? LOOP_DEFAULT : _props_loop, _props_dragFree = props.dragFree, dragFree = _props_dragFree === void 0 ? DRAG_FREE_DEFAULT : _props_dragFree, arrowColorOrVariant = props.arrowColorOrVariant, _props_dotsVariant = props.dotsVariant, dotsVariant = _props_dotsVariant === void 0 ? "primary" : _props_dotsVariant, emblaOptions = props.emblaOptions, onVisible = props.onVisible, ctaTileData = props.ctaTileData, shouldShowDots = props.shouldShowDots, dotsOffsetY = props.dotsOffsetY, _props_showNavigationArrows = props.showNavigationArrows, showNavigationArrows = _props_showNavigationArrows === void 0 ? SHOW_NAVIGATION_ARROWS_DEFAULT : _props_showNavigationArrows, shouldShowDisabledNavigationArrows = props.shouldShowDisabledNavigationArrows, arrowOffsetX = props.arrowOffsetX, freeFlow = props.freeFlow, className = props.className, attachRefOnView = props.attachRefOnView, autoplay = props.autoplay, autoplayOptions = props.autoplayOptions, _props_plugins = props.plugins, plugins = _props_plugins === void 0 ? [] : _props_plugins;
    var viewport = useViewport();
    var strictLocalize = useStrictLocalization().strictLocalize;
    // Generate props for each screen size
    var itemsPerSlideGenerated = generateResponsiveValuesFromProps(itemsPerSlide);
    var itemGapGenerated = generateResponsiveValuesFromProps(itemGap);
    var peekNextPercentGenerated = generateResponsiveValuesFromProps(peekNextPercent);
    var peekPreviousPercentGenerated = generateResponsiveValuesFromProps(peekPreviousPercent);
    var loopGenerated = generateResponsiveValuesFromProps(loop);
    var dragFreeGenerated = generateResponsiveValuesFromProps(dragFree);
    var shouldShowDotsGenerated = generateResponsiveValuesFromProps(shouldShowDots);
    var showNavigationArrowsGenerated = generateResponsiveValuesFromProps(showNavigationArrows);
    // Get current screen size value to see if we should show dots
    var shouldShowDotsResponsive = useResponsiveValue(shouldShowDotsGenerated);
    var showNavigationArrowsResponsive = useResponsiveValue(showNavigationArrowsGenerated);
    var autoplayGenerated = generateResponsiveValuesFromProps(autoplay);
    var shouldAutoplay = useResponsiveValue(autoplayGenerated);
    var hasCTA = shouldAddCTATileWithViewport(items, ctaTileData, itemsPerSlideGenerated, viewport);
    var defaultEmblaOptionsMobile = {
        align: peekPreviousPercentGenerated.mobileAndAbove > 0 ? "center" : "start",
        dragFree: dragFreeGenerated.mobileAndAbove,
        loop: loopGenerated.mobileAndAbove,
        slidesToScroll: itemsPerSlideGenerated.mobileAndAbove
    };
    var defaultEmblaOptionsTablet = {
        align: peekPreviousPercentGenerated.tabletAndAbove > 0 ? "center" : "start",
        loop: ctaTileData ? false : loopGenerated.tabletAndAbove,
        dragFree: dragFreeGenerated.tabletAndAbove,
        slidesToScroll: itemsPerSlideGenerated.tabletAndAbove
    };
    var defaultEmblaOptionsDesktopAndAbove = {
        align: peekPreviousPercentGenerated.desktopAndAbove > 0 ? "center" : "start",
        loop: ctaTileData ? false : loopGenerated.desktopAndAbove,
        dragFree: dragFreeGenerated.desktopAndAbove,
        slidesToScroll: itemsPerSlideGenerated.desktopAndAbove
    };
    var defaultEmblaOptionsXLargeAndAbove = {
        align: peekPreviousPercentGenerated.xLargeAndAbove > 0 ? "center" : "start",
        loop: ctaTileData ? false : loopGenerated.xLargeAndAbove,
        dragFree: dragFreeGenerated.xLargeAndAbove,
        slidesToScroll: itemsPerSlideGenerated.xLargeAndAbove
    };
    var _useState = _sliced_to_array(useState(0), 2), selectedIndex = _useState[0], setSelectedIndex = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), hasScrolled = _useState1[0], setHasScrolled = _useState1[1];
    var _useState2 = _sliced_to_array(useState(showNavigationArrowsResponsive), 2), prevBtnEnabled = _useState2[0], setPrevBtnEnabled = _useState2[1];
    var _useState3 = _sliced_to_array(useState(showNavigationArrowsResponsive), 2), nextBtnEnabled = _useState3[0], setNextBtnEnabled = _useState3[1];
    var _useState4 = _sliced_to_array(useState(function() {
        return new Set(getInitialRenderFlags(viewport, {
            numItems: items.length,
            itemsPerSlideGenerated: itemsPerSlideGenerated,
            peekNextPercentGenerated: peekNextPercentGenerated,
            peekPreviousPercentGenerated: peekPreviousPercentGenerated
        }));
    }), 2), slideRenderFlags = _useState4[0], setSlideRenderFlags = _useState4[1];
    //If the native navigation is disabled, we should disable the wheel gestures as well
    //Since it will make the carousel scrollable even if it's not needed,
    //which will make user interaction less smooth, and stuck the carousel in a weird state
    var isNavigationDisabled = !prevBtnEnabled && !nextBtnEnabled;
    var _obj;
    var _useEmblaCarousel = _sliced_to_array(useEmblaCarousel(_object_spread({
        containScroll: "trimSnaps",
        breakpoints: (_obj = {}, _define_property(_obj, responsiveMediaQueries.mobile({}).replace("@media", ""), defaultEmblaOptionsMobile), _define_property(_obj, responsiveMediaQueries.tablet({}).replace("@media", ""), defaultEmblaOptionsTablet), _define_property(_obj, responsiveMediaQueries.desktopAndAbove({}).replace("@media", ""), defaultEmblaOptionsDesktopAndAbove), _define_property(_obj, responsiveMediaQueries.xLargeAndAbove({}).replace("@media", ""), defaultEmblaOptionsXLargeAndAbove), _obj)
    }, emblaOptions), _to_consumable_array(isNavigationDisabled ? [] : [
        WheelGesturesPlugin()
    ]).concat(_to_consumable_array(shouldAutoplay ? [
        Autoplay(autoplayOptions)
    ] : []), _to_consumable_array(plugins))), 2), emblaRef = _useEmblaCarousel[0], emblaAPI = _useEmblaCarousel[1];
    var _useState5 = _sliced_to_array(useState(false), 2), carouselViewed = _useState5[0], setCarouselViewed = _useState5[1];
    var slideViewFlags = useRef(new Set());
    var itemsRef = useRef(items);
    itemsRef.current = items;
    var onVisibleRef = useRef(onVisible);
    onVisibleRef.current = onVisible;
    var sendViewEvent = useCallback(function(emblaApi) {
        var newItemsInView = new Set();
        emblaApi.slidesInView().forEach(function(value) {
            if (!slideViewFlags.current.has(value)) {
                slideViewFlags.current.add(value);
                newItemsInView.add(value);
            }
        });
        var itemsInView = [];
        if (newItemsInView.size > 0) {
            var _onVisibleRef_current;
            newItemsInView.forEach(function(value) {
                var item = itemsRef.current[value];
                if (item !== undefined) {
                    itemsInView.push(item);
                }
            });
            (_onVisibleRef_current = onVisibleRef.current) === null || _onVisibleRef_current === void 0 ? void 0 : _onVisibleRef_current.call(onVisibleRef, itemsInView);
        }
    }, []);
    var viewRef = useDoActionWhenVisible(function() {
        setCarouselViewed(true);
    });
    var sendInitialViewEvent = useCallback(function(emblaApi) {
        sendViewEvent(emblaApi);
        emblaApi.off("slidesInView", sendInitialViewEvent);
    }, [
        sendViewEvent
    ]);
    var onSlideInViewChange = useCallback(function(emblaApi) {
        setSlideRenderFlags(function(state) {
            if (state.size === itemsRef.current.length) {
                emblaApi.off("slidesInView", onSlideInViewChange);
                return state;
            }
            var addedFlag = false;
            emblaApi.slidesInView().forEach(function(value) {
                if (!state.has(value)) {
                    state.add(value);
                    addedFlag = true;
                }
            });
            if (addedFlag) {
                return new Set(_to_consumable_array(state));
            }
            return state;
        });
    }, []);
    var onSettle = useCallback(function(emblaApi) {
        setHasScrolled(true);
        if (slideViewFlags.current.size === itemsRef.current.length) {
            emblaApi.off("settle", onSettle);
            return;
        }
        sendViewEvent(emblaApi);
    }, [
        sendViewEvent
    ]);
    var onSelect = useCallback(function(emblaApi) {
        if (shouldShowDotsResponsive) {
            setSelectedIndex(emblaApi.selectedScrollSnap());
        }
        setPrevBtnEnabled(emblaApi.canScrollPrev());
        setNextBtnEnabled(emblaApi.canScrollNext());
    }, [
        shouldShowDotsResponsive
    ]);
    var onReInit = useCallback(function(emblaApi) {
        if (!freeFlow) {
            emblaApi.off("slidesInView", onSlideInViewChange);
            emblaApi.on("slidesInView", onSlideInViewChange);
        }
        slideViewFlags.current.clear();
        sendViewEvent(emblaApi);
        onSelect(emblaApi);
    }, [
        freeFlow,
        onSelect,
        onSlideInViewChange,
        sendViewEvent
    ]);
    var scrollPrev = useCallback(function() {
        emblaAPI === null || emblaAPI === void 0 ? void 0 : emblaAPI.scrollPrev();
        setHasScrolled(true);
    }, [
        emblaAPI
    ]);
    var scrollNext = useCallback(function() {
        emblaAPI === null || emblaAPI === void 0 ? void 0 : emblaAPI.scrollNext();
        setHasScrolled(true);
    }, [
        emblaAPI
    ]);
    var scrollTo = useCallback(function(index) {
        var _emblaAPI_scrollTo;
        emblaAPI === null || emblaAPI === void 0 ? void 0 : (_emblaAPI_scrollTo = emblaAPI.scrollTo) === null || _emblaAPI_scrollTo === void 0 ? void 0 : _emblaAPI_scrollTo.call(emblaAPI, index);
        setHasScrolled(true);
    }, [
        emblaAPI
    ]);
    var _useMemo = useMemo(function() {
        if (arrowColorOrVariant === "primary") {
            return {
                arrowVariant: "primary",
                arrowColor: Core.icon.inverse,
                arrowColorHover: Core.icon.inverse
            };
        }
        if (arrowColorOrVariant === "secondary") {
            return {
                arrowVariant: "secondary",
                arrowColor: Core.icon.default,
                arrowColorHover: Core.icon.default
            };
        }
        return {
            arrowVariant: "tertiary",
            arrowColor: arrowColorOrVariant !== null && arrowColorOrVariant !== void 0 ? arrowColorOrVariant : Core.icon.default,
            arrowColorHover: Core.icon.default
        };
    }, [
        arrowColorOrVariant
    ]), arrowVariant = _useMemo.arrowVariant, arrowColor = _useMemo.arrowColor, arrowColorHover = _useMemo.arrowColorHover;
    var _useMemo1 = _sliced_to_array(useMemo(function() {
        var shouldShowPrevButton = showNavigationArrowsResponsive && (shouldShowDisabledNavigationArrows ? hasScrolled || prevBtnEnabled : prevBtnEnabled);
        var shouldShowNextButton = showNavigationArrowsResponsive && (shouldShowDisabledNavigationArrows ? hasScrolled || nextBtnEnabled : nextBtnEnabled);
        return [
            shouldShowPrevButton,
            shouldShowNextButton
        ];
    }, [
        prevBtnEnabled,
        nextBtnEnabled,
        showNavigationArrowsResponsive,
        shouldShowDisabledNavigationArrows,
        hasScrolled
    ]), 2), shouldShowPrevButton = _useMemo1[0], shouldShowNextButton = _useMemo1[1];
    useEffect(function() {
        if (!emblaAPI || !carouselViewed) {
            return;
        }
        if (emblaAPI.slidesInView().length === 0) {
            emblaAPI.on("slidesInView", sendInitialViewEvent);
        } else {
            sendViewEvent(emblaAPI);
        }
    }, [
        carouselViewed,
        emblaAPI,
        sendInitialViewEvent,
        sendViewEvent
    ]);
    useEffect(function() {
        if (!emblaAPI) {
            return;
        }
        onSelect(emblaAPI);
        if (!freeFlow) {
            emblaAPI.on("slidesInView", onSlideInViewChange);
        }
        emblaAPI.on("settle", onSettle);
        emblaAPI.on("select", onSelect);
        emblaAPI.on("reInit", onReInit);
    }, [
        emblaAPI,
        onSlideInViewChange,
        onSelect,
        onSettle,
        onReInit,
        freeFlow
    ]);
    var renderPreviousButton = function() {
        return shouldShowPrevButton ? /*#__PURE__*/ _jsx(IconButton, {
            onClick: scrollPrev,
            "data-test-id": "btn-previous",
            "aria-label": strictLocalize({
                id: "dmlNx/",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Previous Carousel Slide"
                    }
                ]
            }),
            variant: arrowVariant,
            "aria-disabled": !prevBtnEnabled,
            disabled: !prevBtnEnabled,
            className: classNames("f_c_icon_button", "f_c_icon_button_previous"),
            size: "small",
            children: /*#__PURE__*/ _jsx(Chevron, {
                direction: "left"
            })
        }) : null;
    };
    var renderNextButton = function() {
        return shouldShowNextButton ? /*#__PURE__*/ _jsx(IconButton, {
            onClick: scrollNext,
            "data-test-id": "btn-next",
            "aria-label": strictLocalize({
                id: "kHyFNJ",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Next Carousel Slide"
                    }
                ]
            }),
            variant: arrowVariant,
            "aria-disabled": !nextBtnEnabled,
            disabled: !nextBtnEnabled,
            className: classNames("f_c_icon_button", "f_c_icon_button_next"),
            size: "small",
            children: /*#__PURE__*/ _jsx(Chevron, {
                direction: "right"
            })
        }) : null;
    };
    var renderDots = function() {
        if (!shouldShowDotsResponsive || !emblaAPI) {
            return null;
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    size: dotsOffsetY !== null && dotsOffsetY !== void 0 ? dotsOffsetY : "5x"
                }),
                /*#__PURE__*/ _jsx(CarouselDots, {
                    onClick: function(index) {
                        scrollTo(index);
                    },
                    currentImageIndex: selectedIndex,
                    numValidImages: emblaAPI.scrollSnapList().length,
                    dotSize: "large",
                    colorVariant: dotsVariant === "secondary" ? "light" : "dark"
                })
            ]
        });
    };
    var slideStylesWidth = freeFlow ? "f_c_slide_width_free_flow" : [
        "f_c_slide_width_fixed_base",
        "f_c_slide_width_fixed_variables"
    ];
    var slideClassName = classNames(slideStylesWidth, "f_c_slide_gap");
    var shouldAttachEmblaRef = !attachRefOnView || carouselViewed;
    var _peekNextPercentGenerated_xLargeAndAbove, _peekNextPercentGenerated_desktopAndAbove, _peekNextPercentGenerated_tabletAndAbove, _peekNextPercentGenerated_mobileAndAbove, _peekPreviousPercentGenerated_xLargeAndAbove, _peekPreviousPercentGenerated_desktopAndAbove, _peekPreviousPercentGenerated_tabletAndAbove, _peekPreviousPercentGenerated_mobileAndAbove;
    var _obj1;
    return /*#__PURE__*/ _jsxs("div", {
        ref: viewRef,
        className: classNames("f_c_carousel_wrapper_base", "f_c_carousel_wrapper_mobile", className, shouldShowPrevButton || shouldShowNextButton ? "f_c_show_arrow_buttons_mobile" : undefined),
        style: assignInlineVars((_obj1 = {}, // Arrow variables
        _define_property(_obj1, "--f-c-arrow-color", arrowColor), _define_property(_obj1, "--f-c-arrow-color-hover", arrowColorHover), _define_property(_obj1, "--f-c-arrow-offset-x", (arrowOffsetX !== null && arrowOffsetX !== void 0 ? arrowOffsetX : "8") + "px"), // Item per slide variables
        _define_property(_obj1, "--f-c-item-per-slide-xlarge", itemsPerSlideGenerated.xLargeAndAbove.toString()), _define_property(_obj1, "--f-c-item-per-slide-desktop", itemsPerSlideGenerated.desktopAndAbove.toString()), _define_property(_obj1, "--f-c-item-per-slide-tablet", itemsPerSlideGenerated.tabletAndAbove.toString()), _define_property(_obj1, "--f-c-item-per-slide-mobile", itemsPerSlideGenerated.mobileAndAbove.toString()), // Item gap variables
        _define_property(_obj1, "--f-c-item-gap-xlarge", itemGapGenerated.xLargeAndAbove + "px"), _define_property(_obj1, "--f-c-item-gap-desktop", itemGapGenerated.desktopAndAbove + "px"), _define_property(_obj1, "--f-c-item-gap-tablet", itemGapGenerated.tabletAndAbove + "px"), _define_property(_obj1, "--f-c-item-gap-mobile", itemGapGenerated.mobileAndAbove + "px"), // Peek next variables
        _define_property(_obj1, "--f-c-peek-next-xlarge", (((_peekNextPercentGenerated_xLargeAndAbove = peekNextPercentGenerated.xLargeAndAbove) !== null && _peekNextPercentGenerated_xLargeAndAbove !== void 0 ? _peekNextPercentGenerated_xLargeAndAbove : 0) / 100).toString()), _define_property(_obj1, "--f-c-peek-next-desktop", (((_peekNextPercentGenerated_desktopAndAbove = peekNextPercentGenerated.desktopAndAbove) !== null && _peekNextPercentGenerated_desktopAndAbove !== void 0 ? _peekNextPercentGenerated_desktopAndAbove : 0) / 100).toString()), _define_property(_obj1, "--f-c-peek-next-tablet", (((_peekNextPercentGenerated_tabletAndAbove = peekNextPercentGenerated.tabletAndAbove) !== null && _peekNextPercentGenerated_tabletAndAbove !== void 0 ? _peekNextPercentGenerated_tabletAndAbove : 0) / 100).toString()), _define_property(_obj1, "--f-c-peek-next-mobile", (((_peekNextPercentGenerated_mobileAndAbove = peekNextPercentGenerated.mobileAndAbove) !== null && _peekNextPercentGenerated_mobileAndAbove !== void 0 ? _peekNextPercentGenerated_mobileAndAbove : 0) / 100).toString()), // Peek previous variables
        _define_property(_obj1, "--f-c-peek-prev-xlarge", (((_peekPreviousPercentGenerated_xLargeAndAbove = peekPreviousPercentGenerated.xLargeAndAbove) !== null && _peekPreviousPercentGenerated_xLargeAndAbove !== void 0 ? _peekPreviousPercentGenerated_xLargeAndAbove : 0) / 100).toString()), _define_property(_obj1, "--f-c-peek-prev-desktop", (((_peekPreviousPercentGenerated_desktopAndAbove = peekPreviousPercentGenerated.desktopAndAbove) !== null && _peekPreviousPercentGenerated_desktopAndAbove !== void 0 ? _peekPreviousPercentGenerated_desktopAndAbove : 0) / 100).toString()), _define_property(_obj1, "--f-c-peek-prev-tablet", (((_peekPreviousPercentGenerated_tabletAndAbove = peekPreviousPercentGenerated.tabletAndAbove) !== null && _peekPreviousPercentGenerated_tabletAndAbove !== void 0 ? _peekPreviousPercentGenerated_tabletAndAbove : 0) / 100).toString()), _define_property(_obj1, "--f-c-peek-prev-mobile", (((_peekPreviousPercentGenerated_mobileAndAbove = peekPreviousPercentGenerated.mobileAndAbove) !== null && _peekPreviousPercentGenerated_mobileAndAbove !== void 0 ? _peekPreviousPercentGenerated_mobileAndAbove : 0) / 100).toString()), // Has peek next variables
        _define_property(_obj1, "--f-c-has-peek-next-xlarge", peekNextPercentGenerated.xLargeAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-has-peek-next-desktop", peekNextPercentGenerated.desktopAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-has-peek-next-tablet", peekNextPercentGenerated.tabletAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-has-peek-next-mobile", peekNextPercentGenerated.mobileAndAbove ? "1" : "0"), // Has peek previous variables
        _define_property(_obj1, "--f-c-has-peek-prev-xlarge", peekPreviousPercentGenerated.xLargeAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-has-peek-prev-desktop", peekPreviousPercentGenerated.desktopAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-has-peek-prev-tablet", peekPreviousPercentGenerated.tabletAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-has-peek-prev-mobile", peekPreviousPercentGenerated.mobileAndAbove ? "1" : "0"), // Loop variables
        _define_property(_obj1, "--f-c-loop-xlarge", loopGenerated.xLargeAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-loop-desktop", loopGenerated.desktopAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-loop-tablet", loopGenerated.tabletAndAbove ? "1" : "0"), _define_property(_obj1, "--f-c-loop-mobile", loopGenerated.mobileAndAbove ? "1" : "0"), _obj1)),
        children: [
            renderPreviousButton(),
            /*#__PURE__*/ _jsx("div", {
                className: "f_c_outer_container",
                ref: shouldAttachEmblaRef ? emblaRef : undefined,
                "data-test-id": "carousel-slides",
                children: /*#__PURE__*/ _jsxs(Flex, {
                    className: "f_c_inner_container",
                    children: [
                        items.map(function(item, index) {
                            return /*#__PURE__*/ _jsx("div", {
                                className: slideClassName,
                                "data-index": index,
                                children: slideRenderFlags.has(index) || freeFlow ? renderItem(item, index) : null
                            }, index);
                        }),
                        hasCTA && isNotUndefined(ctaTileData) ? /*#__PURE__*/ _jsx("div", {
                            className: slideClassName,
                            "data-index": items.length,
                            style: {
                                alignSelf: "center"
                            },
                            children: slideRenderFlags.size >= items.length ? /*#__PURE__*/ _jsx(CTATile, {
                                data: ctaTileData
                            }, "cta-tile") : null
                        }, "cta") : null
                    ]
                })
            }),
            renderNextButton(),
            renderDots()
        ]
    });
};
